import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { MarkdownRenderer } from '../common/MarkdownRenderer';
import {
  Search, BookOpen, ChevronDown, ChevronLeft, ChevronRight,
  X, Plus, Edit3, Trash2, Heading1, Heading2,
  Bold, Italic, List, Code, AlertCircle, AlertTriangle,
  Table as TableIcon, Paperclip, Image as ImageIcon,
  Download, RefreshCw, Sparkles, FolderPlus, ArrowLeft
} from 'lucide-react';

interface HelpAttachment {
  name: string;
  key: string;
  size: number;
  type: string;
  url?: string;
}

interface HelpArticle {
  id: number;
  slug?: string;
  title: string;
  category: string;
  content: string;
  excerpt?: string;
  order_index?: number;
  sort_order?: number;
  tags?: string;
  keywords?: string;
  attachments?: HelpAttachment[] | string;
}

interface HelpCenterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Beépített alapértelmezett kategória szótár
const KNOWN_CATEGORY_LABELS: Record<string, string> = {
  basics: 'Első lépések & Alapok',
  kezdes: 'Első lépések & Alapok',
  data_entry: 'Adatlapok & Mellékletek',
  feltoltes: 'Adat- és fájlkezelés',
  import_export: 'ZIP Import & Export',
  rbac: 'Felhasználók & Jogosultságok',
  public_portal: 'Publikus Portál',
  sync: 'Tri-Target Szinkronizáció',
  szinkron: 'Tri-Target Szinkronizáció',
  admin: 'Rendszerkezelés & Erőforrások',
  arculat: 'Arculat és portál',
  support: 'Hibajegyek & Ügyfélszolgálat',
  compliance: 'Jogszabályi megfelelőség',
  jogszabalyok: 'Jogszabályi megfelelőség',
};

export const formatCategoryLabel = (cat: string): string => {
  if (!cat) return 'Egyéb témakör';
  return KNOWN_CATEGORY_LABELS[cat] || cat;
};

const formatBytes = (bytes: number): string => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const parseAttachments = (att: any): HelpAttachment[] => {
  if (!att) return [];
  let list: any[] = [];
  if (Array.isArray(att)) list = att;
  else if (typeof att === 'string') {
    try {
      const parsed = JSON.parse(att);
      list = Array.isArray(parsed) ? parsed : [];
    } catch {
      list = [];
    }
  }
  return list.map(item => ({
    name: item.name || item.filename || 'melléklet',
    key: item.key || item.r2_key || '',
    size: item.size || 0,
    type: item.type || item.mime_type || 'application/octet-stream',
    url: item.url || (item.key || item.r2_key ? `/api/files/${encodeURIComponent(item.key || item.r2_key)}` : undefined),
  }));
};

export const HelpCenterModal: React.FC<HelpCenterModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'superadmin' || user?.role === 'tenant_admin';

  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedArticleId, setExpandedArticleId] = useState<number | null>(null);

  // Lightbox preview for images
  const [previewImage, setPreviewImage] = useState<{ url: string; name: string } | null>(null);

  // Superadmin editor modal state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<'edit' | 'preview'>('edit');
  const [editingArticle, setEditingArticle] = useState<HelpArticle | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formCategory, setFormCategory] = useState('data_entry');
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [formExcerpt, setFormExcerpt] = useState('');
  const [formContent, setFormContent] = useState('');
  const [formTags, setFormTags] = useState('');
  const [formAttachments, setFormAttachments] = useState<HelpAttachment[]>([]);
  const [savingArticle, setSavingArticle] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoryTabsRef = useRef<HTMLDivElement>(null);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await ApiClient.getHelpArticles();
      if (res.ok && res.data) {
        setArticles(res.data);
      }
    } catch (e) {
      console.error('Hiba a súgócikkek lekérésekor:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchArticles();
    }
  }, [isOpen]);

  // Dinamikus kategória-számláló és fülek
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    articles.forEach(a => {
      const cat = a.category || 'basics';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    return counts;
  }, [articles]);

  const dynamicCategories = useMemo(() => {
    const entries = Object.entries(categoryCounts).map(([catId, count]) => ({
      id: catId,
      label: formatCategoryLabel(catId),
      count,
    }));
    return [
      { id: 'all', label: 'Összes témakör', count: articles.length },
      ...entries,
    ];
  }, [categoryCounts, articles.length]);

  // Automatikus görgetés az aktív kategóriára
  useEffect(() => {
    if (categoryTabsRef.current) {
      const activeBtn = categoryTabsRef.current.querySelector(`[data-cat-id="${selectedCategory}"]`) as HTMLElement;
      if (activeBtn) {
        activeBtn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  }, [selectedCategory]);

  // Témakörök fülsorának görgetése (balra / jobbra)
  const scrollCategories = (direction: 'left' | 'right') => {
    if (categoryTabsRef.current) {
      const delta = direction === 'left' ? -260 : 260;
      categoryTabsRef.current.scrollBy({ left: delta, behavior: 'smooth' });
    }
  };

  // Szerkesztőben kiválasztható kategóriák (ismert lista + meglévő cikkek egyedi témái)
  const selectableCategories = useMemo(() => {
    const map = new Map<string, string>();
    // Alapértelmezett bevált kategóriák
    map.set('data_entry', 'Adatlapok & Mellékletek');
    map.set('basics', 'Első lépések & Alapok');
    map.set('import_export', 'ZIP Import & Export');
    map.set('rbac', 'Felhasználók & Jogosultságok');
    map.set('public_portal', 'Publikus Portál');
    map.set('sync', 'Tri-Target Szinkronizáció');
    map.set('admin', 'Rendszerkezelés & Erőforrások');
    map.set('support', 'Hibajegyek & Ügyfélszolgálat');
    map.set('compliance', 'Jogszabályi megfelelőség');

    // Meglévő egyedi kategóriák a cikkekből
    articles.forEach(a => {
      if (a.category && !map.has(a.category)) {
        map.set(a.category, formatCategoryLabel(a.category));
      }
    });

    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [articles]);

  const handlePrevFormCategory = () => {
    const idx = selectableCategories.findIndex(c => c.id === formCategory);
    if (idx > 0) {
      setFormCategory(selectableCategories[idx - 1].id);
    } else {
      setFormCategory(selectableCategories[selectableCategories.length - 1].id);
    }
  };

  const handleNextFormCategory = () => {
    const idx = selectableCategories.findIndex(c => c.id === formCategory);
    if (idx < selectableCategories.length - 1 && idx >= 0) {
      setFormCategory(selectableCategories[idx + 1].id);
    } else {
      setFormCategory(selectableCategories[0].id);
    }
  };

  if (!isOpen) return null;

  const filteredArticles = articles.filter(a => {
    if (selectedCategory !== 'all' && a.category !== selectedCategory) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    const catName = formatCategoryLabel(a.category).toLowerCase();
    return (
      a.title.toLowerCase().includes(q) ||
      catName.includes(q) ||
      (a.excerpt && a.excerpt.toLowerCase().includes(q)) ||
      (a.tags && a.tags.toLowerCase().includes(q)) ||
      (a.keywords && a.keywords.toLowerCase().includes(q)) ||
      a.content.toLowerCase().includes(q)
    );
  });

  const handleOpenNew = () => {
    setEditingArticle(null);
    setFormTitle('');
    setFormCategory('data_entry');
    setIsCustomCategory(false);
    setFormExcerpt('');
    setFormContent('');
    setFormTags('');
    setFormAttachments([]);
    setEditorTab('edit');
    setIsEditorOpen(true);
  };

  const handleOpenEdit = (a: HelpArticle, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingArticle(a);
    setFormTitle(a.title);

    // Ha az adott kategória nem szerepel a szabványos listában, kapcsoljunk egyedi módra
    const isStandard = selectableCategories.some(c => c.id === a.category);
    setIsCustomCategory(!isStandard);
    setFormCategory(a.category);

    setFormExcerpt(a.excerpt || '');
    setFormContent(a.content);
    setFormTags(a.tags || a.keywords || '');
    setFormAttachments(parseAttachments(a.attachments));
    setEditorTab('edit');
    setIsEditorOpen(true);
  };

  const handleDelete = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Biztosan törölni szeretnéd ezt a súgócikket?')) return;
    try {
      await ApiClient.deleteHelpArticle(id);
      setArticles(prev => prev.filter(a => a.id !== id));
      if (expandedArticleId === id) setExpandedArticleId(null);
    } catch (e: any) {
      alert('Hiba: ' + e.message);
    }
  };

  // Helper to insert markdown text at cursor position
  const insertMarkdownText = (prefix: string, suffix: string = '', defaultText: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentText = textarea.value;
    const selectedText = currentText.substring(start, end) || defaultText;

    const newText =
      currentText.substring(0, start) +
      prefix +
      selectedText +
      suffix +
      currentText.substring(end);

    setFormContent(newText);

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + prefix.length, start + prefix.length + selectedText.length);
    }, 50);
  };

  // Handle uploading attachment / image in editor
  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingAttachment(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (file.size > 20 * 1024 * 1024) {
          alert(`A(z) ${file.name} mérete meghaladja a 20 MB-os limitet!`);
          continue;
        }

        const res = await ApiClient.uploadHelpAttachment(file, file.name);
        if (res.ok && res.data) {
          const uploaded = res.data;
          setFormAttachments(prev => [...prev, uploaded]);

          // Auto insert markdown image or link at cursor
          const isImg = uploaded.type?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(uploaded.name);
          const snippet = isImg
            ? `\n![${uploaded.name}](${uploaded.url})\n`
            : `\n[📎 Letöltés: ${uploaded.name}](${uploaded.url})\n`;

          insertMarkdownText(snippet);
        } else {
          alert(`Hiba a feltöltéskor: ${res.error || 'Ismeretlen hiba'}`);
        }
      }
    } catch (err: any) {
      alert('Feltöltési hiba: ' + err.message);
    } finally {
      setUploadingAttachment(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveArticle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle.trim() || !formContent.trim()) return;
    const finalCategory = (formCategory || '').trim() || 'data_entry';

    setSavingArticle(true);
    try {
      const payload = {
        title: formTitle.trim(),
        category: finalCategory,
        excerpt: formExcerpt.trim(),
        content: formContent.trim(),
        tags: formTags.trim(),
        keywords: formTags.trim(),
        attachments: formAttachments,
      };
      if (editingArticle) {
        await ApiClient.updateHelpArticle(editingArticle.id, payload);
      } else {
        await ApiClient.createHelpArticle(payload);
      }
      setIsEditorOpen(false);
      await fetchArticles();
      setSelectedCategory('all');
    } catch (e: any) {
      alert('Hiba mentéskor: ' + e.message);
    } finally {
      setSavingArticle(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-150 overflow-y-auto">
      <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-4xl w-full h-[85vh] min-h-[580px] max-h-[92vh] flex flex-col shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {/* Fejléc */}
        <div className="px-5 sm:px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0 bg-slate-50/50 dark:bg-slate-950/50">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-500/10 text-indigo-600 dark:text-cyan-400 rounded-xl border border-indigo-500/20">
              <BookOpen className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <span>Rendszer Súgó & Tudásbázis (.md)</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-cyan-300 font-mono">
                  {articles.length} útmutató
                </span>
              </h2>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Formázott Markdown útmutatók, folyamatleírások, képrészletek és jogszabályi tudnivalók
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isSuperAdmin && (
              <button
                type="button"
                onClick={handleOpenNew}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl text-xs font-semibold shadow-xs transition cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Új útmutató (.md)</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Keresőmező és Léptethető Kategória fülek */}
        <div className="p-4 border-b border-slate-100 dark:border-slate-800 space-y-3 shrink-0 bg-white dark:bg-slate-900">
          <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
            <Search className="w-4 h-4 text-slate-400 shrink-0" />
            <input
              type="text"
              placeholder="Keressen kulcsszó, folyamat vagy téma alapján (pl. Excel, PDF, szinkron, jogosultságok, 2FA)..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-xs text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none"
            />
            {search && (
              <button onClick={() => setSearch('')} className="text-slate-400 hover:text-slate-600">
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Témakörök léptethető fülsora balra-jobbra nyilakkal */}
          <div className="flex items-center gap-1.5">
            {/* Balra léptető gomb */}
            <button
              type="button"
              onClick={() => scrollCategories('left')}
              className="p-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 hover:bg-indigo-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-cyan-400 shrink-0 transition cursor-pointer shadow-2xs"
              title="Előző témakörök görgetése"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            {/* Görgethető témakör lista */}
            <div
              ref={categoryTabsRef}
              onWheel={(e) => {
                if (categoryTabsRef.current && e.deltaY !== 0) {
                  e.preventDefault();
                  categoryTabsRef.current.scrollLeft += e.deltaY;
                }
              }}
              className="flex-1 flex items-center gap-1.5 overflow-x-auto py-1 scroll-smooth scrollbar-none"
            >
              {dynamicCategories.map(c => (
                <button
                  key={c.id}
                  data-cat-id={c.id}
                  onClick={() => setSelectedCategory(c.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition cursor-pointer flex items-center gap-1.5 shrink-0 ${
                    selectedCategory === c.id
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                  }`}
                >
                  <span>{c.label}</span>
                  <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono ${
                    selectedCategory === c.id
                      ? 'bg-indigo-700/80 text-white'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                  }`}>
                    {c.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Jobbra léptető gomb */}
            <button
              type="button"
              onClick={() => scrollCategories('right')}
              className="p-1.5 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 hover:bg-indigo-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 hover:text-indigo-600 dark:hover:text-cyan-400 shrink-0 transition cursor-pointer shadow-2xs"
              title="További témakörök görgetése"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Cikkek listája & kinyitható részletei */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center py-12 text-slate-400 text-xs flex flex-col items-center gap-2">
              <RefreshCw className="w-5 h-5 animate-spin text-indigo-500" />
              <span>Útmutatók betöltése...</span>
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-12 text-slate-400 text-xs">
              Nem található a kiválasztott témakörben vagy keresési feltételnek megfelelő útmutató.
            </div>
          ) : (
            filteredArticles.map(a => {
              const isExpanded = expandedArticleId === a.id;
              const attachments = parseAttachments(a.attachments);

              return (
                <div
                  key={a.id}
                  className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 overflow-hidden shadow-2xs transition"
                >
                  <div
                    onClick={() => setExpandedArticleId(isExpanded ? null : a.id)}
                    className="p-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition"
                  >
                    <div className="flex-1 min-w-0 space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-950/50 text-indigo-700 dark:text-cyan-300">
                          {formatCategoryLabel(a.category)}
                        </span>
                        {(a.tags || a.keywords) && (
                          <span className="text-[10px] text-slate-400 font-mono">
                            #{(a.tags || a.keywords || '').split(',').map(s => s.trim()).filter(Boolean).slice(0, 4).join(' #')}
                          </span>
                        )}
                        {attachments.length > 0 && (
                          <span className="text-[10px] text-indigo-600 dark:text-cyan-400 flex items-center gap-1 font-mono">
                            <Paperclip className="w-3 h-3" />
                            {attachments.length} melléklet
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                        {a.title}
                      </h4>
                      {a.excerpt && (
                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">
                          {a.excerpt}
                        </p>
                      )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {isSuperAdmin && (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            onClick={(e) => handleOpenEdit(a, e)}
                            className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                            title="Szerkesztés"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => handleDelete(a.id, e)}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-lg transition"
                            title="Törlés"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                      <div className={`p-1 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                        <ChevronDown className="w-4 h-4" />
                      </div>
                    </div>
                  </div>

                  {/* Kinyitott Tartalom Markdown Rendereléssel */}
                  {isExpanded && (
                    <div className="p-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/30 text-xs text-slate-800 dark:text-slate-200 leading-relaxed font-sans space-y-4">
                      <MarkdownRenderer
                        content={a.content}
                        onImageClick={(url, alt) => setPreviewImage({ url, name: alt || a.title })}
                      />

                      {/* Letölthető mellékletek listája */}
                      {attachments.length > 0 && (
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-2">
                          <div className="flex items-center gap-1.5 text-[11px] font-bold text-slate-500 dark:text-slate-400">
                            <Paperclip className="w-3.5 h-3.5 text-indigo-500" />
                            <span>Csatolt fájlok és segédanyagok ({attachments.length})</span>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {attachments.map((att, idx) => {
                              const isImg = att.type?.startsWith('image/') || /\.(png|jpe?g|webp|gif)$/i.test(att.name);
                              const rawUrl = att.url || `/api/files/${encodeURIComponent(att.key)}`;
                              const displayUrl = ApiClient.getAuthFileUrl(rawUrl, false);
                              const downloadUrl = ApiClient.getAuthFileUrl(rawUrl, true);

                              return (
                                <div
                                  key={idx}
                                  className="flex items-center justify-between gap-2 p-2.5 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xs hover:border-indigo-500/50 transition"
                                >
                                  <div className="flex items-center gap-2 min-w-0">
                                    {isImg ? (
                                      <ImageIcon className="w-4 h-4 text-emerald-500 shrink-0" />
                                    ) : (
                                      <Paperclip className="w-4 h-4 text-indigo-500 shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                      <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                        {att.name}
                                      </p>
                                      <p className="text-[10px] text-slate-400 font-mono">
                                        {formatBytes(att.size)}
                                      </p>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1.5 shrink-0">
                                    {isImg && (
                                      <button
                                        type="button"
                                        onClick={() => setPreviewImage({ url: displayUrl, name: att.name })}
                                        className="p-1 text-slate-400 hover:text-indigo-600 rounded transition"
                                        title="Kép megtekintése"
                                      >
                                        <ImageIcon className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    <a
                                      href={downloadUrl}
                                      download={att.name}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="p-1 text-slate-400 hover:text-indigo-600 rounded transition"
                                      title="Letöltés"
                                    >
                                      <Download className="w-3.5 h-3.5" />
                                    </a>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Lábjegyzet */}
        <div className="px-5 sm:px-6 py-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-[11px] text-slate-400 shrink-0 bg-slate-50/50 dark:bg-slate-950/50">
          <span className="flex items-center gap-1">
            <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
            <span>KözzéTér Pro. Integrált Tudásbázis (Markdown formátum & Dinamikus Témakörök)</span>
          </span>
          <span>Nem találta a választ? Nyisson hibajegyet a fejlécben található 🎫 ikonnal!</span>
        </div>
      </div>

      {/* Superadmin szerkesztő modal Rich Markdown szerkesztővel, témakör léptetéssel és előnézettel */}
      {isEditorOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-3 sm:p-4 bg-slate-900/70 backdrop-blur-xs overflow-y-auto">
          <div className="bg-white dark:bg-slate-900 rounded-2xl max-w-3xl w-full h-[88vh] min-h-[580px] max-h-[92vh] flex flex-col p-5 sm:p-6 shadow-2xl border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in-95 duration-150">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <div className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-indigo-500" />
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  {editingArticle ? 'Súgócikk szerkesztése (.md)' : 'Új Markdown súgócikk készítése'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsEditorOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveArticle} className="flex-1 flex flex-col min-h-0 overflow-y-auto pt-3 space-y-3">
              {/* Alap metaadatok */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 shrink-0">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Cím <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="pl. 0 byte-os Excel feltöltési hiba elhárítása"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>

                {/* Témakör választó, léptető és új téma felvétele */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                      Témakör <span className="text-rose-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setIsCustomCategory(!isCustomCategory);
                        if (!isCustomCategory) {
                          setFormCategory('');
                        } else {
                          setFormCategory('data_entry');
                        }
                      }}
                      className="text-[11px] font-semibold text-indigo-600 dark:text-cyan-400 hover:underline flex items-center gap-1 cursor-pointer"
                      title={isCustomCategory ? 'Visszaváltás meglévő listára' : 'Új egyedi témakör létrehozása'}
                    >
                      {isCustomCategory ? (
                        <>
                          <ArrowLeft className="w-3 h-3" />
                          <span>Meglévők</span>
                        </>
                      ) : (
                        <>
                          <FolderPlus className="w-3 h-3" />
                          <span>+ Új téma</span>
                        </>
                      )}
                    </button>
                  </div>

                  {isCustomCategory ? (
                    <div>
                      <input
                        type="text"
                        required
                        placeholder="pl. Közbeszerzések, DPO..."
                        value={formCategory}
                        onChange={(e) => setFormCategory(e.target.value)}
                        className="w-full px-3 py-2 text-xs rounded-xl border-2 border-indigo-500 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                        autoFocus
                      />
                      <span className="text-[10px] text-indigo-600 dark:text-cyan-400 block mt-0.5">
                        Új témakörként jelenik meg.
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={handlePrevFormCategory}
                        className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-slate-700 text-slate-500 hover:text-indigo-600 dark:hover:text-cyan-400 transition shrink-0 cursor-pointer"
                        title="Előző témakör"
                      >
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <select
                        value={formCategory}
                        onChange={(e) => {
                          if (e.target.value === '__NEW__') {
                            setIsCustomCategory(true);
                            setFormCategory('');
                          } else {
                            setFormCategory(e.target.value);
                          }
                        }}
                        className="flex-1 min-w-0 px-2.5 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none cursor-pointer"
                      >
                        {selectableCategories.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.label}
                          </option>
                        ))}
                        <option value="__NEW__">➕ + Új témakör megadása...</option>
                      </select>
                      <button
                        type="button"
                        onClick={handleNextFormCategory}
                        className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 hover:bg-indigo-50 dark:hover:bg-slate-700 text-slate-500 hover:text-indigo-600 dark:hover:text-cyan-400 transition shrink-0 cursor-pointer"
                        title="Következő témakör"
                      >
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 shrink-0">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Rövid összefoglaló (kártyán megjelenő leírás)
                  </label>
                  <input
                    type="text"
                    placeholder="Egy-két mondatos lényeg..."
                    value={formExcerpt}
                    onChange={(e) => setFormExcerpt(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                    Címkék (vesszővel elválasztva)
                  </label>
                  <input
                    type="text"
                    placeholder="excel, hiba, letoltes, xlsx, folyamat"
                    value={formTags}
                    onChange={(e) => setFormTags(e.target.value)}
                    className="w-full px-3 py-2 text-xs rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-2 focus:ring-indigo-500 outline-none"
                  />
                </div>
              </div>

              {/* Szerkesztés / Élő Előnézet Fülváltó és Markdown Eszköztár */}
              <div className="flex-1 flex flex-col min-h-0 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-slate-50/50 dark:bg-slate-950/40">
                <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2 bg-white dark:bg-slate-900 shrink-0">
                  {/* Fülek */}
                  <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 p-0.5 rounded-xl text-xs">
                    <button
                      type="button"
                      onClick={() => setEditorTab('edit')}
                      className={`px-3 py-1 rounded-lg font-semibold transition cursor-pointer ${
                        editorTab === 'edit'
                          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-cyan-400 shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                      }`}
                    >
                      Szerkesztés (Markdown)
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditorTab('preview')}
                      className={`px-3 py-1 rounded-lg font-semibold transition cursor-pointer ${
                        editorTab === 'preview'
                          ? 'bg-white dark:bg-slate-700 text-indigo-600 dark:text-cyan-400 shadow-xs'
                          : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                      }`}
                    >
                      Élő Előnézet (.md)
                    </button>
                  </div>

                  {/* Markdown Toolbar (csak szerkesztés módban) */}
                  {editorTab === 'edit' && (
                    <div className="flex items-center gap-1 flex-wrap">
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('## ', '', 'Címsor')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Főcím (H2)"
                      >
                        <Heading1 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('### ', '', 'Alcím')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Alcím (H3)"
                      >
                        <Heading2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('**', '**', 'vastag')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Félkövér"
                      >
                        <Bold className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('*', '*', 'dőlt')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Dőlt"
                      >
                        <Italic className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('- ', '', 'Felsorolás')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Lista"
                      >
                        <List className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('```\n', '\n```', 'kód vagy parancs')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Kódblokk"
                      >
                        <Code className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('> [!NOTE]\n> ', '', 'Fontos tudnivaló')}
                        className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                        title="Kiemelés (Megjegyzés)"
                      >
                        <AlertCircle className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('> [!WARNING]\n> ', '', 'Figyelem, kötelező lépés!')}
                        className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                        title="Figyelmeztetés"
                      >
                        <AlertTriangle className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => insertMarkdownText('\n| Oszlop 1 | Oszlop 2 |\n|---|---|\n| Érték A | Érték B |\n')}
                        className="p-1.5 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                        title="Táblázat"
                      >
                        <TableIcon className="w-3.5 h-3.5" />
                      </button>

                      {/* Kép vagy fájl feltöltés és azonnali beillesztés */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        multiple
                        accept="image/*,.pdf,.docx,.xlsx,.txt"
                        className="hidden"
                        onChange={handleUploadFile}
                      />
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploadingAttachment}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-950/50 text-indigo-600 dark:text-cyan-400 border border-indigo-200 dark:border-indigo-800/60 hover:bg-indigo-100 text-xs font-medium transition cursor-pointer"
                        title="Kép vagy dokumentum feltöltése a cikkhez"
                      >
                        <ImageIcon className="w-3.5 h-3.5" />
                        <span>{uploadingAttachment ? 'Feltöltés...' : 'Kép / Fájl beszúrása'}</span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Szerkesztési terület vagy Előnézet */}
                <div className="flex-1 overflow-y-auto p-3">
                  {editorTab === 'edit' ? (
                    <textarea
                      ref={textareaRef}
                      required
                      value={formContent}
                      onChange={(e) => setFormContent(e.target.value)}
                      placeholder="# Cikk címe&#10;&#10;Írja le a tudnivalókat tetszőleges Markdown formázással...&#10;&#10;## 1. Lépés: Feltöltés&#10;- Válassza ki a fájlt&#10;- Kattintson a mentésre&#10;&#10;> [!NOTE]&#10;> A 0 byte méretű fájlokat a rendszer automatikusan szűri."
                      className="w-full h-full min-h-[260px] bg-transparent text-xs text-slate-900 dark:text-white font-mono outline-none resize-none leading-relaxed"
                    />
                  ) : (
                    <div className="space-y-4">
                      <div className="border-b border-slate-200 dark:border-slate-800 pb-2">
                        <span className="text-[10px] font-mono text-indigo-600 dark:text-cyan-400 uppercase font-bold tracking-wider">
                          Előnézet: {formTitle || 'Cím nélkül'} ({formatCategoryLabel(formCategory)})
                        </span>
                        {formExcerpt && (
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">
                            {formExcerpt}
                          </p>
                        )}
                      </div>
                      <MarkdownRenderer
                        content={formContent || '*Nincs még beírt tartalom.*'}
                        onImageClick={(url, alt) => setPreviewImage({ url, name: alt })}
                      />
                    </div>
                  )}
                </div>

                {/* Feltöltött csatolmányok minilistája a szerkesztőben */}
                {formAttachments.length > 0 && (
                  <div className="px-3 py-2 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 flex flex-wrap items-center gap-1.5 shrink-0 text-xs">
                    <span className="text-[11px] text-slate-400 flex items-center gap-1">
                      <Paperclip className="w-3 h-3" />
                      <span>Csatolt fájlok:</span>
                    </span>
                    {formAttachments.map((att, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-[11px]"
                      >
                        <span className="truncate max-w-[140px]">{att.name}</span>
                        <button
                          type="button"
                          onClick={() => setFormAttachments(prev => prev.filter((_, i) => i !== idx))}
                          className="text-slate-400 hover:text-rose-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Mentés gombok */}
              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsEditorOpen(false)}
                  className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl text-xs font-semibold transition cursor-pointer"
                >
                  Mégse
                </button>
                <button
                  type="submit"
                  disabled={savingArticle || uploadingAttachment}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-xs font-semibold shadow-md transition cursor-pointer"
                >
                  {savingArticle ? 'Mentés...' : 'Súgócikk mentése'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox modal képnagyításhoz */}
      {previewImage && (
        <div
          className="fixed inset-0 z-70 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-150"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 text-xs shrink-0">
              <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-md">
                {previewImage.name}
              </span>
              <div className="flex items-center gap-2">
                <a
                  href={ApiClient.getAuthFileUrl(previewImage.url, true)}
                  download={previewImage.name}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 px-2.5 py-1 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-lg transition"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>Letöltés</span>
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewImage(null)}
                  className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            <div className="p-2 overflow-auto flex items-center justify-center bg-slate-950/90">
              <img
                src={ApiClient.getAuthFileUrl(previewImage.url, false)}
                alt={previewImage.name}
                className="max-h-[80vh] w-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
